import React, { useMemo } from "react";
import { useUnidades } from "context/UnidadeContext";
import { useRelatorios } from "context/RelatorioContext";
import { useAprendizado } from "context/AprendizadoContext";
import { useDevices } from "context/DeviceContext";

interface LinhaMeta {
  unidadeNome: string;
  diferencaPercentual: number;
  diferencaAbsoluta: number;
  periodo: string;
  data: string;
  dataCompleta: Date; 
}

export default function RelatorioMetas() {
  const { unidades } = useUnidades();
  const { relatorios } = useRelatorios();
  const { aprendizadoData } = useAprendizado();
  const { devices } = useDevices();

  const dadosTabela = useMemo(() => {
    const datasParaProcessar = [...new Set(relatorios.map(r => r.Data))];
    const dadosBase: LinhaMeta[] = [];

    datasParaProcessar.forEach(dataStr => {
      const dataAtual = new Date(dataStr + "T12:00:00");
      const diaSemanaAtual = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sab."][dataAtual.getDay()];

      unidades.forEach(unidade => {
        const device = devices.find(d => d.id === unidade.dispositivoId);
        if (!device) return;

        const periodosDoDevice = Object.keys(device.expedientes || {});

        periodosDoDevice.forEach(periodo => {
          const aprendizado = aprendizadoData.find(a => a.dispositivoId === unidade.dispositivoId);
          if (!aprendizado) return;

          const dataInicioAprendizado = new Date(aprendizado.dataCadastro);
          const dataFimAprendizado = new Date(dataInicioAprendizado);
          dataFimAprendizado.setDate(dataInicioAprendizado.getDate() + 28);

          const relatoriosAprendizado = relatorios.filter(r =>
            r.dispositivoId === unidade.dispositivoId &&
            r.DiaSemana === diaSemanaAtual &&
            r.expediente === periodo &&
            new Date(r.Data) >= dataInicioAprendizado &&
            new Date(r.Data) <= dataFimAprendizado
          );

          const metaDiaria = relatoriosAprendizado.length > 0
            ? relatoriosAprendizado.reduce((sum, r) => sum + r.contador, 0) / relatoriosAprendizado.length
            : 0;

          if (metaDiaria === 0) return;

          const contagemDoDia = relatorios
            .filter(r => 
              r.dispositivoId === unidade.dispositivoId && 
              r.Data === dataStr &&
              r.expediente === periodo
            )
            .reduce((sum, r) => sum + r.contador, 0);
          
          if (contagemDoDia > 0) {
              const diferencaAbsoluta = contagemDoDia - metaDiaria;
              const diferencaPercentual = (diferencaAbsoluta / metaDiaria) * 100;

              dadosBase.push({
                  unidadeNome: unidade.nome,
                  diferencaPercentual: parseFloat(diferencaPercentual.toFixed(1)),
                  diferencaAbsoluta: Math.round(diferencaAbsoluta),
                  periodo: periodo,
                  data: dataAtual.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
                  dataCompleta: dataAtual,
              });
          }
        });
      });
    });

    return dadosBase
      .sort((a, b) => b.dataCompleta.getTime() - a.dataCompleta.getTime())
      .slice(0, 6);
  }, [unidades, relatorios, aprendizadoData, devices]);

  return (
    <div className="w-full p-4 bg-white rounded-xl shadow h-full flex flex-col">
      <h2 className="text-lg font-bold text-navy-700 mb-4 shrink-0">
        Análise de Metas
      </h2>

      {dadosTabela.length === 0 ? (
        <div className="flex items-center justify-center h-full text-center text-gray-500">
          Nenhuma meta recente para exibir.
        </div>
      ) : (
        <div className="overflow-y-auto">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-sm font-bold text-gray-600">
                <th>Unidade</th>
                <th>Meta</th>
                <th>Quantidade</th>
                <th>Período</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
            {dadosTabela.map((item, index) => {
                const metaExata = item.diferencaPercentual === 0;
                const metaPositiva = item.diferencaPercentual > 0;
                const corMeta = metaExata || metaPositiva ? 'text-green-500' : 'text-red-500';

                return (
                <tr key={index} className="border-t">
                    <td className="py-3">
                    <span className="text-sm font-bold text-navy-700">
                        {item.unidadeNome}
                    </span>
                    </td>
                    <td className={`py-3 text-sm font-bold ${corMeta}`}>
                    {metaExata 
                        ? "100%" 
                        : `${metaPositiva ? '+' : ''}${item.diferencaPercentual}%`
                    }
                    </td>
                    <td className="py-3 text-sm font-bold text-navy-700">
                    {metaExata 
                        ? "Meta Exata" 
                        : `${item.diferencaAbsoluta} Pessoas`
                    }
                    </td>
                    <td className="py-3 text-sm font-bold text-navy-700">
                    {item.periodo}
                    </td>
                    <td className="py-3 text-sm font-bold text-navy-700">
                    {item.data}
                    </td>
                </tr>
                );
            })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
